import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { fetchCancellationTrackingReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

const CancellationTrackingReport = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
  const [selectedClinician, setSelectedClinician] = useState("all");

  const {
    data: reportData,
    loading,
    error,
  } = useSelector((state) => state.reports.cancellationTracking || { data: null, loading: false, error: null });

  const clinicians = useSelector((state) => state.clinicians?.data || []);

  const data = reportData || {};
  const summary = data.summary || {};
  const byClinician = data.byClinician || [];
  const byType = data.byType || {};

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(`[Cancellation Tracking Report] Generating report for ${startDate} to ${endDate}, clinician: ${selectedClinician}`);
    dispatch(fetchCancellationTrackingReport({ startDate, endDate, clinicianId: selectedClinician }));
  };

  const handleDownload = () => {
    if (byClinician.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const exportData = byClinician.map((item) => ({
      "Clinician": item.clinicianName || "Unknown",
      "Total": item.total || 0,
      // Cancellations
      "Cancelled": item.cancellations?.["Cancelled"] || 0,
      "Cancelled - No Fee": item.cancellations?.["Cancelled - No Fee"] || 0,
      "Cancelled - Charge Fee": item.cancellations?.["Cancelled - Charge Fee"] || 0,
      "Cancelled - Clinician": item.cancellations?.["Cancelled - Clinician"] || 0,
      "Cancelled - Client": item.cancellations?.["Cancelled - Client"] || 0,
      // No Shows
      "No Show": item.noShows?.["No Show"] || 0,
      "No Show - No Fee": item.noShows?.["No Show - No Fee"] || 0,
      "No Show - Charge Fee": item.noShows?.["No Show - Charge Fee"] || 0,
    }));

    // Add summary row
    exportData.push({
      "Clinician": "TOTAL",
      "Total": summary.totalCancellations + summary.totalNoShows,
      "Cancelled": byType["Cancelled"] || 0,
      "Cancelled - No Fee": byType["Cancelled - No Fee"] || 0,
      "Cancelled - Charge Fee": byType["Cancelled - Charge Fee"] || 0,
      "Cancelled - Clinician": byType["Cancelled - Clinician"] || 0,
      "Cancelled - Client": byType["Cancelled - Client"] || 0,
      "No Show": byType["No Show"] || 0,
      "No Show - No Fee": byType["No Show - No Fee"] || 0,
      "No Show - Charge Fee": byType["No Show - Charge Fee"] || 0,
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cancellation Tracking");
    XLSX.writeFile(workbook, `Cancellation_Tracking_${startDate}_to_${endDate}.xlsx`);

    toast.success("Report downloaded successfully");
    console.log(`[Cancellation Tracking Report] Excel export completed`);
  };

  const clinicianColumns = [
    { header: "Clinician", accessor: "clinicianName" },
    {
      header: "Total",
      accessor: "total",
      className: "font-semibold"
    },
    {
      header: "Cancelled",
      accessor: (row) => row.cancellations?.["Cancelled"] || 0,
      className: "text-gray-700"
    },
    {
      header: "Cancelled - No Fee",
      accessor: (row) => row.cancellations?.["Cancelled - No Fee"] || 0,
      className: "text-blue-600"
    },
    {
      header: "Cancelled - Charge Fee",
      accessor: (row) => row.cancellations?.["Cancelled - Charge Fee"] || 0,
      className: "text-orange-600"
    },
    {
      header: "Cancelled - Clinician",
      accessor: (row) => row.cancellations?.["Cancelled - Clinician"] || 0,
      className: "text-purple-600"
    },
    {
      header: "Cancelled - Client",
      accessor: (row) => row.cancellations?.["Cancelled - Client"] || 0,
      className: "text-indigo-600"
    },
    {
      header: "No Show",
      accessor: (row) => row.noShows?.["No Show"] || 0,
      className: "text-red-600 font-medium"
    },
    {
      header: "No Show - No Fee",
      accessor: (row) => row.noShows?.["No Show - No Fee"] || 0,
      className: "text-red-400"
    },
    {
      header: "No Show - Charge Fee",
      accessor: (row) => row.noShows?.["No Show - Charge Fee"] || 0,
      className: "text-red-700 font-medium"
    },
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Date Range and Clinician Filter Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Cancellation Tracking Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="clinician" className="block text-sm font-medium text-gray-700 mb-1">
              Clinician
            </label>
            <select
              id="clinician"
              value={selectedClinician}
              onChange={(e) => setSelectedClinician(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Clinicians</option>
              {clinicians.map((clinician) => (
                <option key={clinician._id} value={clinician._id}>
                  {clinician.firstName} {clinician.lastName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      </form>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading cancellation data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && summary && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Cancellations</h4>
              <p className="text-3xl font-bold text-orange-600">{summary.totalCancellations || 0}</p>
              <p className="text-xs text-gray-500 mt-1">All cancellation types</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total No Shows</h4>
              <p className="text-3xl font-bold text-red-600">{summary.totalNoShows || 0}</p>
              <p className="text-xs text-gray-500 mt-1">All no-show types</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Grand Total</h4>
              <p className="text-3xl font-bold text-gray-900">{(summary.totalCancellations || 0) + (summary.totalNoShows || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">Combined cancellations & no-shows</p>
            </div>
          </div>

          {/* Breakdown by Type Cards */}
          <div className="bg-white p-6 rounded-sm shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Breakdown by Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
              <div className="border border-gray-200 rounded-sm p-4">
                <h5 className="text-xs font-medium text-gray-500 mb-1">Cancelled</h5>
                <p className="text-2xl font-bold text-gray-700">{byType["Cancelled"] || 0}</p>
              </div>
              <div className="border border-blue-200 rounded-sm p-4 bg-blue-50">
                <h5 className="text-xs font-medium text-blue-600 mb-1">Cancelled - No Fee</h5>
                <p className="text-2xl font-bold text-blue-600">{byType["Cancelled - No Fee"] || 0}</p>
              </div>
              <div className="border border-orange-200 rounded-sm p-4 bg-orange-50">
                <h5 className="text-xs font-medium text-orange-600 mb-1">Cancelled - Charge Fee</h5>
                <p className="text-2xl font-bold text-orange-600">{byType["Cancelled - Charge Fee"] || 0}</p>
              </div>
              <div className="border border-purple-200 rounded-sm p-4 bg-purple-50">
                <h5 className="text-xs font-medium text-purple-600 mb-1">Cancelled - Clinician</h5>
                <p className="text-2xl font-bold text-purple-600">{byType["Cancelled - Clinician"] || 0}</p>
              </div>
              <div className="border border-indigo-200 rounded-sm p-4 bg-indigo-50">
                <h5 className="text-xs font-medium text-indigo-600 mb-1">Cancelled - Client</h5>
                <p className="text-2xl font-bold text-indigo-600">{byType["Cancelled - Client"] || 0}</p>
              </div>
              <div className="border border-red-200 rounded-sm p-4 bg-red-50">
                <h5 className="text-xs font-medium text-red-600 mb-1">No Show</h5>
                <p className="text-2xl font-bold text-red-600">{byType["No Show"] || 0}</p>
              </div>
              <div className="border border-red-200 rounded-sm p-4 bg-red-50">
                <h5 className="text-xs font-medium text-red-400 mb-1">No Show - No Fee</h5>
                <p className="text-2xl font-bold text-red-400">{byType["No Show - No Fee"] || 0}</p>
              </div>
              <div className="border border-red-300 rounded-sm p-4 bg-red-100">
                <h5 className="text-xs font-medium text-red-700 mb-1">No Show - Charge Fee</h5>
                <p className="text-2xl font-bold text-red-700">{byType["No Show - Charge Fee"] || 0}</p>
              </div>
            </div>
          </div>

          {/* Clinician Breakdown Table */}
          {byClinician.length > 0 && (
            <div className="bg-white shadow-sm rounded-sm">
              <div className="p-6">
                <Table
                  columns={clinicianColumns}
                  data={byClinician}
                  title="Cancellations by Clinician"
                  description={`Breakdown of all cancellation and no-show types by treating clinician from ${format(new Date(startDate), "MMM dd, yyyy")} to ${format(new Date(endDate), "MMM dd, yyyy")}`}
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: handleDownload,
                    },
                  ]}
                />
              </div>
            </div>
          )}

          {/* No Data Message */}
          {byClinician.length === 0 && (
            <div className="bg-white p-6 rounded-sm shadow-sm text-center">
              <p className="text-gray-500">No cancellations or no-shows found for the selected criteria.</p>
            </div>
          )}
        </>
      )}

      {/* Initial State Message */}
      {!loading && (!summary || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <p className="text-gray-500">Select a date range and click "Generate Report" to view cancellation data.</p>
        </div>
      )}
    </div>
  );
};

export default CancellationTrackingReport;
